import React, { useMemo } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { decodeParam, decodeParamList, encodeParam } from "Utilities/urlEncoder";
import { useGetBooksCategoryQuery } from "Services/CategoryApiSlice";
import { useGetEcomProductDetailByIdQuery } from "Services/ProductApiSlice";
import { useGetEventByIdQuery } from "Services/EventApiSlice";
import { useGetGlossaryByIdQuery } from "Services/GlossaryApiSlice";
import { useGetHistoryByIdQuery } from "Services/HistoryApiSlice";

const PLANT = process.env.REACT_APP_PLANT;

const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pathnames = location.pathname.split("/").filter((x) => x);
  
  // Fetch categories to resolve IDs to Names
  const { data: categoriesData } = useGetBooksCategoryQuery();

  // Handle Product Detail Page specifically for Hierarchical Breadcrumbs
  const pid = searchParams.get("pid");
  const { data: ecomProductDetail, isFetching } = useGetEcomProductDetailByIdQuery(
    { plant: PLANT || "", productId: decodeParam(pid || "") },
    { skip: !pid || !PLANT || !(location.pathname.includes("/product") || location.pathname.includes("/reviews")) }
  );

  const productData = ecomProductDetail?.productDetail;
  
  // Dynamic labels for other detail pages
  const eid = searchParams.get("eid");
  const { data: eventDetail } = useGetEventByIdQuery(
    { id: Number(decodeParam(eid || "")), plant: PLANT || "" },
    { skip: !eid || !PLANT || !location.pathname.includes("/event-detail") }
  );

  const gid = searchParams.get("gid");
  const { data: galleryDetail } = useGetGlossaryByIdQuery(
    { id: Number(decodeParam(gid || "")), plant: PLANT || "" },
    { skip: !gid || !PLANT || !location.pathname.includes("/gallery-detail") }
  );

  const hid = searchParams.get("hid");
  const { data: historyDetail } = useGetHistoryByIdQuery(
    { id: Number(decodeParam(hid || "")), plant: PLANT || "" },
    { skip: !hid || !PLANT || !location.pathname.includes("/history-detail") }
  );

  const getBreadcrumbLabel = (name: string, isLast: boolean): string => {
    const routeLabels: { [key: string]: string } = {
      product: "Product Detail",
      cart: "Shopping Cart",
      checkout: "Checkout",
      events: "Events",
      gallery: "Gallery",
      history: "History",
      wishlist: "My Wishlist",
      "my-profile": "My Profile",
      "books-listing": "Books",
      authors: "Authors",
      language: "Language",
      academic: "Academic",
      merchandise: "Merchandise",
      "track-order": "Track Order",
      "store-locator": "Store Locator",
      "about-us": "About Us",
      "contact-us": "Contact Us",
      "faq": "FAQ",
      "privacy-policy": "Privacy Policy",
      "terms-and-conditions": "Terms & Conditions",
      "return-policy": "Return Policy",
      "event-detail": "Event Detail",
      "gallery-detail": "Gallery Detail",
      "history-detail": "History Detail",
      "history-timeline": "Timeline",
      "shop": "All Categories",
      "sale": "Exclusive Sale",
      "blogs": "Our Blogs",
      "new-collections": "New Arrivals",
      "top-sellers": "Bestsellers",
      login: "Sign In",
      register: "Sign Up",
      "otp-login": "Sign In",
      category: "Category",
      reviews: "Review",
    };

    if (routeLabels[name]) return routeLabels[name];

    const decoded = decodeParam(name);
    
    if (categoriesData) {
       const matchedCat = categoriesData.find(cat => cat.categoryId === decoded);
       if (matchedCat) return matchedCat.categoryName;
    }

    return decoded
      .replace(/-/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getBreadcrumbs = useMemo(() => {
    let breadcrumbs = [{ label: "Home", path: "/" }];
    
    if ((location.pathname.includes("/product") || location.pathname.includes("/reviews")) && productData) {
       // Hierarchical Breadcrumbs for Product Detail or Reviews
       if (productData.categoryWrapper?.categoryCode) {
         const catId = productData.categoryWrapper.categoryCode;
         const catName = productData.categoryWrapper.categoryName || 
                         categoriesData?.find(cat => cat.categoryId === catId)?.categoryName || 
                         "Category";

         breadcrumbs.push({ 
           label: catName, 
           path: `/books-listing?category=${encodeParam(catId)}` 
         });
       }
       
       const productObj = productData.productWrapper?.product;
       const subId = productObj?.subClassId;
       let subName = productObj?.subClassName;

       if (subId && subId !== "none") {
         // Smart resolve subcategory name if missing or generic
         if ((!subName || subName.toLowerCase() === "none") && categoriesData) {
            for (const cat of categoriesData) {
              const matchedSub = cat.subClasses?.find(sub => 
                sub.subClassId === subId || subId === `${cat.categoryId}_${sub.subClassId}`
              );
              if (matchedSub) {
                subName = matchedSub.subClassName;
                break;
              }
            }
         }

         if (subName && subName.toLowerCase() !== "none") {
           const fullSubId = `${productData.categoryWrapper?.categoryCode || ""}_${subId}`;
           breadcrumbs.push({ 
             label: subName, 
             path: `/books-listing?category=${encodeParam(productData.categoryWrapper?.categoryCode || "")}&subCategory=${encodeParam(fullSubId)}` 
           });
         }
       }

       const productTitle = productData.productWrapper?.product?.itemDesc || 
                            productData.productWrapper?.product?.title || 
                            "Product Detail";

       breadcrumbs.push({ 
         label: productTitle, 
         path: `/product?pid=${pid}` 
       });

       if (location.pathname.includes("/reviews")) {
         breadcrumbs.push({
           label: "Review",
           path: location.pathname + location.search
         });
       }
       
       return breadcrumbs;
    }

    pathnames.forEach((name, index) => {
      const path = `/${pathnames.slice(0, index + 1).join("/")}`;
      
      // Inject parent breadcrumbs for specific sub-pages
      if (name === "invoice-preview") {
        breadcrumbs.push({ label: "Order Summary", path: "/order-summary" });
      } else if (name === "event-detail") {
        breadcrumbs.push({ label: "Events", path: "/events" });
      } else if (name === "gallery-detail") {
        breadcrumbs.push({ label: "Gallery", path: "/gallery" });
      } else if (name === "history-detail") {
        breadcrumbs.push({ label: "History", path: "/history" });
      }

      if (!["event-detail", "gallery-detail", "history-detail"].includes(name)) {
        breadcrumbs.push({
          label: getBreadcrumbLabel(name, index === pathnames.length - 1),
          path,
        });
      }
    });

    const params: string[] = [];
    const category = searchParams.get("category");
    const author = searchParams.get("author");
    const academic = searchParams.get("academic");
    const language = searchParams.get("language");
    const merchandise = searchParams.get("merchandise");
    const subCategoryVal = searchParams.get("subCategory");

    if (category) {
      const decodedCatIds = decodeParamList(category);
      const categoryNames = decodedCatIds.map(catId =>
        categoriesData?.find(c => c.categoryId === catId)?.categoryName || catId
      );
      params.push(`category=${category}`);
      breadcrumbs.push({
        label: categoryNames.join(", "),
        path: `${location.pathname}?${params.join("&")}`,
      });
    }

    if (subCategoryVal) {
      const decodedSubIds = decodeParamList(subCategoryVal);
      const subNames = decodedSubIds.map(decodedSubId => {
        let subIdOnly = decodedSubId;
        if (categoriesData) {
          const matchedCat = categoriesData.find(cat => decodedSubId.startsWith(`${cat.categoryId}_`));
          if (matchedCat) {
            subIdOnly = decodedSubId.substring(matchedCat.categoryId.length + 1);
          } else {
            const firstUnderscore = decodedSubId.indexOf("_");
            if (firstUnderscore !== -1) subIdOnly = decodedSubId.substring(firstUnderscore + 1);
          }
        } else {
          const firstUnderscore = decodedSubId.indexOf("_");
          if (firstUnderscore !== -1) subIdOnly = decodedSubId.substring(firstUnderscore + 1);
        }
        let subName = decodedSubId;
        if (categoriesData) {
          for (const cat of categoriesData) {
            const matchedSub = cat.subClasses.find(sub => sub.subClassId === subIdOnly);
            if (matchedSub) {
              subName = matchedSub.subClassName;
              break;
            }
          }
        }
        return (subName === "none" || subName === "None") ? "None" : subName;
      });
      params.push(`subCategory=${subCategoryVal}`);
      breadcrumbs.push({
        label: subNames.join(", "),
        path: `${location.pathname}?${params.join("&")}`,
      });
    }

    if (author) {
      params.push(`author=${author}`);
      breadcrumbs.push({ label: decodeParamList(author).join(", "), path: `${location.pathname}?${params.join("&")}` });
    }
    if (academic) {
      params.push(`academic=${academic}`);
      breadcrumbs.push({ label: decodeParamList(academic).join(", "), path: `${location.pathname}?${params.join("&")}` });
    }
    if (language) {
      params.push(`language=${language}`);
      breadcrumbs.push({ label: decodeParamList(language).join(", "), path: `${location.pathname}?${params.join("&")}` });
    }
    if (merchandise) {
      params.push(`merchandise=${merchandise}`);
      breadcrumbs.push({ label: decodeParamList(merchandise).join(", "), path: `${location.pathname}?${params.join("&")}` });
    }

    const search = searchParams.get("search");
    if (search) {
      params.push(`search=${search}`);
      breadcrumbs.push({ label: `Search: ${decodeURIComponent(search)}`, path: `${location.pathname}?${params.join("&")}` });
    }

    if (location.pathname.includes("/gallery-detail") && gid) {
      const hdr = galleryDetail?.results?.glossaryHdr || 
                  galleryDetail?.results?.[0]?.glossaryHdr || 
                  galleryDetail?.results?.[0] || 
                  galleryDetail?.results;
      const galleryName = hdr?.glossaryName || "Gallery Detail";
      breadcrumbs.push({ label: galleryName, path: location.pathname + location.search });
    } else if (location.pathname.includes("/event-detail") && eid) {
      const eventName = eventDetail?.name || eventDetail?.eventName || "Event Detail";
      breadcrumbs.push({ label: eventName, path: location.pathname + location.search });
    } else if (location.pathname.includes("/history-detail") && hid) {
      const historyTitle = historyDetail?.title || "History Detail";
      breadcrumbs.push({ label: historyTitle, path: location.pathname + location.search });
    }

    return breadcrumbs.filter((bc, index, self) => {
       if (bc.label.toLowerCase() === "category" && index < self.length - 1) return false;
       return true;
    });
  }, [location.pathname, location.search, searchParams, pathnames, categoriesData, productData, eventDetail, galleryDetail, historyDetail]);

  if (location.pathname === "/" || location.pathname.includes("/checkout")) return null;

  // Prevent generic or stale breadcrumb flicker on product detail pages while data is loading/fetching
  if ((location.pathname.includes("/product") || location.pathname.includes("/reviews")) && pid && (isFetching || !productData)) {
    return null;
  }

  return (
    <nav className="bg-transparent py-4 px-4 sm:px-8 max-w-[1440px] mx-auto w-full">
      <ol className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-500">
        {getBreadcrumbs.map((bc, index) => {
          const isLast = index === getBreadcrumbs.length - 1;
          return (
            <React.Fragment key={index}>
              {index > 0 && <li className="text-gray-400">/</li>}
              <li>
                {isLast ? (
                  <span className="text-vintageText font-semibold capitalize">{bc.label}</span>
                ) : (
                  <Link to={bc.path} className="hover:text-vintageText transition-colors capitalize">{bc.label}</Link>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
